import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/common/tabs";
import { DictType } from "@pma/types";
import { Card, CardContent } from "@components/common/card";
import FilesView from "@components/files/files-view";
import { useAuth } from "@stores/auth-store";
import useTranslator from "@hooks/use-translator";
import { cn } from "@pma/utils/helpers";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { SERVER_URL, TASK_BY_CURRENT_USER_ENDPOINT } from "@pma/utils/api-routes";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useGisTasks } from "@pma/stores/tasks-store";
import { ProjectTasksTabs } from "./project-tasks-tab";
import { ProjectFinancialTabs } from "./project-financials-tab";
import TabViewSettings from "./tab-view-settings";
import { useTabsStore } from "@pma/stores/tabs-store";
import WorkDiariesList from "./work-diaries-list";
import { ProjectAnalyticsDashboard } from "@components/analytics/project-analytics-dashboard";

interface Props {
  project: DictType;
  revalidate: CallableFunction;
}

const projectTabOption = [
  {
    label: "Tasks",
    disabled: false,
  },
  {
    label: "Finance",
    disabled: false,
  },
  {
    label: "Engineering",
    disabled: false,
  },
  {
    label: "Files",
    disabled: false,
  },
  {
    label: "Analytics",
    disabled: false,
  },
];

export const ProjectTabs = ({ project, revalidate }: Props) => {
  const { translate, locale } = useTranslator();
  const { authConfig, setTabManager } = useAuth();
  const { tabs: stateTabs } = useTabsStore();

  const { setGisTasks } = useGisTasks();
  const [searchParam] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const newParams = new URLSearchParams(searchParam);

  const orgId = authConfig?.last_organization_id;

  const projectStateTabs = (stateTabs["project-detail"] || []) as string[];

  const hasStateTabs = projectStateTabs?.length > 0;

  const accessToken = parseCookies()[ACCESS_TOKEN];
  const projectId = project?.id;

  const queryKey = searchParam.get("revalidate");

  const getProjectTasks = async () => {
    const response = await axios.post(
      SERVER_URL + TASK_BY_CURRENT_USER_ENDPOINT,
      {
        project_id: projectId,
        show_created_by: true,
        organization_id: orgId,
        paging_per_page: 50,
        show_deleted: false
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,

          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  };

  const {
    data: projectTasks,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["get-tasks", orgId, projectId, queryKey],
    queryFn: getProjectTasks,
  });

  const sortedTasks =
    projectTasks?.items?.sort((a: DictType, b: DictType) => a.created_at - b.created_at) || [];

  const paramTaskId = searchParam.get("task");

  const isHebrew = locale === "he";

  const allStateTabs = authConfig?.tabs;
  const projectDetailActiveTab = allStateTabs ? allStateTabs["project-detail"] : undefined;
  const activeTabs: DictType = projectDetailActiveTab || {
    main: "tasks",
    sub: "groups",
  };
  const activeMainTab = activeTabs?.main || "tasks";
  const showTable = !!paramTaskId || activeMainTab === "tasks" || undefined;

  useEffect(() => {
    if (queryKey === "tasks") {
      refetch();
      newParams.delete("revalidate");

      navigate(`${pathname + `?${newParams.toString()}`}`);
    }
  }, [queryKey]);

  useEffect(() => {
    if (sortedTasks) {
      // Save tasks with GIS point in state to be accessed in <GisMap />
      const taskWithGis = sortedTasks.filter((task: DictType) => task?._gis);

      setGisTasks(taskWithGis);
    }
  }, [sortedTasks?.length]);

  return (
    <Card className='w-full space-y-6 overflow-x-hidden border-none shadow-none'>
      {/* Main Navigation */}
      <CardContent className='!p-0'>
        <Tabs
          value={activeMainTab}
          className={cn("relative w-full max-w-[90vw] overflow-x-auto", isHebrew ? "rtl" : "ltr")}
        >
          <TabsList className='flex w-full items-center justify-center bg-transparent'>
            <TabsTrigger
              value='tasks'
              className={cn(
                "rounded-none border-primary bg-transparent data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none",
                projectStateTabs?.includes("Tasks") ? "flex" : !hasStateTabs ? "flex" : "hidden",
              )}
              onClick={() => {
                setTabManager({
                  group: "project-detail",
                  main: "tasks",
                  sub: "groups",
                  subChild: undefined,
                });
                navigate(pathname);
              }}
            >
              {translate("Tasks")}
            </TabsTrigger>
            <TabsTrigger
              value='finance'
              className={cn(
                "rounded-none border-primary bg-transparent data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none",
                projectStateTabs?.includes("Finance") ? "flex" : !hasStateTabs ? "flex" : "hidden",
              )}
              onClick={() => {
                setTabManager({
                  group: "project-detail",
                  main: "finance",
                  sub: "boq",
                  subChild: "bills",
                });
                navigate(pathname);
              }}
            >
              {translate("Finance")}
            </TabsTrigger>
            <TabsTrigger
              value='engineering'
              className={cn(
                "rounded-none border-primary bg-transparent data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none",
                projectStateTabs?.includes("Engineering")
                  ? "flex"
                  : !hasStateTabs
                    ? "flex"
                    : "hidden",
              )}
              onClick={() => {
                setTabManager({
                  group: "project-detail",
                  main: "engineering",
                  sub: "workDiaries",
                  subChild: undefined,
                });
                navigate(pathname);
              }}
            >
              {translate("Engineering")}
            </TabsTrigger>
            <TabsTrigger
              value='files'
              className={cn(
                "rounded-none border-primary bg-transparent data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none",
                projectStateTabs?.includes("Files") ? "flex" : !hasStateTabs ? "flex" : "hidden",
              )}
              onClick={() => {
                setTabManager({
                  group: "project-detail",
                  main: "files",
                  sub: undefined,
                  subChild: undefined,
                });
                navigate(pathname);
              }}
            >
              {translate("Files")}
            </TabsTrigger>

            <TabsTrigger
              value='analytics'
              className={cn(
                "rounded-none border-primary bg-transparent data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none",
                projectStateTabs?.includes("Analytics") ? "flex" : !hasStateTabs ? "flex" : "hidden",
              )}
              onClick={() => {
                setTabManager({
                  group: "project-detail",
                  main: "analytics",
                  sub: undefined,
                  subChild: undefined,
                });
                navigate(pathname);
              }}
            >
              {translate("Analytics")}
            </TabsTrigger>

            <TabViewSettings options={projectTabOption} context='project-detail' />
          </TabsList>
          <TabsContent value='groups' forceMount={showTable} className={cn("p-0")}>
            <ProjectTasksTabs project={project} revalidate={revalidate} />
          </TabsContent>
          <TabsContent value='finance' className={cn("p-0")}>
            <ProjectFinancialTabs project={project} revalidate={revalidate} />
          </TabsContent>
          <TabsContent value='engineering' className={cn("p-0")}>
            <WorkDiariesList project={project} />
          </TabsContent>
          <TabsContent
            value='files'
            className={cn("h-[80vh] w-full gap-3 pb-2 pt-0", paramTaskId && "hidden")}
          >
            <FilesView project={project} />
          </TabsContent>
          <TabsContent value='analytics' className={cn("p-0 pt-4")}>
            {projectId && (
              <ProjectAnalyticsDashboard
                projectId={projectId}
                projectName={project?.title}
              />
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
